import { useCallback, useEffect } from 'react';
import {
  AssessmentScore,
  AssessmentTreatmentSteps,
  AssessmentTreatment,
  Testimonials,
  TextButton
} from '@cerebral-inc/wode-screener-questions';
import { useHistory } from 'react-router-dom';
import {
  ANXIETY_ASSESSMENT_RESULT,
  ANXIETY_ASSESSMENT_SCORING
} from 'constants/localStorageKey';
import { getStaticFilePath } from 'components/shared/utils';
import { AssessmentWrapper } from 'components/shared/styled';
import BusinessYoungMan from './images/business-young-man.png';

const AnxietyAssessmentResult = () => {
  const history = useHistory();
  const data = localStorage.getItem(ANXIETY_ASSESSMENT_RESULT);
  const steps = [
    {
      title: 'Basic info',
      description:
        'Create your account with some basic information — name, contact info, and some context about your experience with mental health care.'
    },
    {
      title: 'Choose a treatment plan',
      description:
        'Learn a bit more about therapy and medication and then choose the right service to support you. You can add or switch services later for any reason.'
    },
    {
      title: 'Choose a clinician + book a session',
      description:
        'See all available clinicians in your area by specialty and availability. You can read their bios, see their full calendars, and book your first session.'
    }
  ];

  const backToIntro = useCallback(() => {
    history.push('/anxiety-self-test');
  }, [history]);

  useEffect(() => {
    return () => {
      if (history.action === 'POP') {
        backToIntro();
      }
    };
  }, [history, backToIntro]);

  if (!data) {
    backToIntro();
    return <></>;
  }

  const handleContinue = () => {
    window.open(`${process.env.REACT_APP_CLIENT_APP_URL}`, '_self');
  };

  return (
    <AssessmentWrapper>
      <AssessmentScore
        score={Number(localStorage.getItem(ANXIETY_ASSESSMENT_SCORING))}
        maxScore={21}
        title="Your anxiety score is:"
        description="Experiencing symptoms is a common occurrence. Discover ways to manage and reduce symptoms through Cerebral."
        serviceLine="anxiety"
        onSubmit={handleContinue}
        buttonText="Continue"
      />
      <AssessmentTreatment
        title="Continue with basic info to see if treatment is right for you"
        description="The professionals at Cerebral can provide a medical evaluation and appropriate treatment options to help you take control of your mental health."
        onSubmit={handleContinue}
        ctaText="Continue"
        subscript="Next steps"
        treatmentOptions={[
          {
            title: 'Highly-rated therapists',
            description: (
              <p>
                We thoroughly vet and only verify top clinical experts to join
                Cerebral. 99% of our therapists have a{' '}
                <strong>4.8 approval rating or higher</strong> from their
                clients.
              </p>
            )
          },
          {
            title: 'Cognitive behavioral therapy (CBT)',
            description:
              'CBT helps identify specific challenges in your thought patterns and develop skills to respond to them more effectively. One of the most common and effective forms of talk therapy.'
          },
          {
            title: 'Prescription medication from licensed prescribers',
            description:
              'Explore medication options with a licensed prescriber who will determine whether a prescription is appropriate.'
          }
        ]}
        cerebralRx="Medication is shipped directly to you — and, with CerebralRx,  can cost up to 80% less than retail prices"
        image={getStaticFilePath(BusinessYoungMan)}
      />
      <AssessmentTreatmentSteps
        steps={steps}
        onSubmit={handleContinue}
        ctaText="Continue"
      />
      <Testimonials />
      <TextButton
        title="Get to a good place"
        description="Join Cerebral to get the professional support you need to live a happy, healthy, and productive life."
        ctaText="Continue"
        onSubmit={handleContinue}
      />
    </AssessmentWrapper>
  );
};

export default AnxietyAssessmentResult;
